import { type DeviceName, deviceNames } from '@athena-lib/device';
import { objectEntries, objectFromEntries } from '@athena-lib/object-utils';
import { valueForPrevious } from '@athena-lib/responsive-utils';
import type {
  GridColumnSpan,
  ResponsiveGridColumnSpan,
  GridColumns,
  ResponsiveGridColumns,
} from './types';

export const containerWidthFromColumns = (
  explicitGridItemColumnSpan: GridColumnSpan | ResponsiveGridColumnSpan | undefined,
  columns: GridColumns | ResponsiveGridColumns | undefined,
): Record<DeviceName, number> => {
  if (columns && explicitGridItemColumnSpan) {
    return containerWidthFromExplicitGridItemColumnSpan(explicitGridItemColumnSpan, columns);
  }

  let entries: [DeviceName, number][];

  if (!columns) {
    entries = deviceNames.map((name) => [name, 1]);
  } else if (typeof columns === 'number') {
    entries = deviceNames.map((name) => [name, 1 / (columns as number)]);
  } else {
    entries = objectEntries(columns).map(([name, value]) => [name, 1 / value!]);
  }

  return {
    // To ensure that we have widths for all breakpoints
    ...objectFromEntries(deviceNames.map((name) => [name, 1])),
    ...objectFromEntries(entries),
  };
};

function containerWidthFromExplicitGridItemColumnSpan(
  explicitGridItemColumnSpan: GridColumnSpan | ResponsiveGridColumnSpan,
  columns: GridColumns | ResponsiveGridColumns,
): Record<DeviceName, number> {
  if (typeof columns === 'number') {
    const columnFactor = 12 / columns;

    if (typeof explicitGridItemColumnSpan === 'number') {
      const width = (explicitGridItemColumnSpan * columnFactor) / 12;
      return objectFromEntries(deviceNames.map((name) => [name, width]));
    }

    if (typeof explicitGridItemColumnSpan === 'object') {
      return objectFromEntries(
        deviceNames.map((name) => {
          const defaultExplicitGridItemColumnSpan =
            valueForPrevious(name, explicitGridItemColumnSpan) ?? 1;

          const width =
            ((explicitGridItemColumnSpan[name] ?? defaultExplicitGridItemColumnSpan) *
              columnFactor) /
            12;
          return [name, width];
        }),
      );
    }
  }

  // We don't currently handle if columns is an object and explicitGridItemColumnSpan is a number
  // or if both are objects, since we don't have a use case for that.

  return { sm: 1, md: 1, lg: 1 };
}
